import RouteInterceptDialog from "../../components/RouteIntercept/RouteInterceptDialog";
import { useContext, useEffect, useState } from "react";
import { ToggleSwitch } from "@livingmap/core-ui-v2";
import { useLocation, useParams } from "react-router-dom";

import { usePostRouteData } from "./hooks/usePostRouteData";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { useRoutingUserActiveFloor } from "../../hooks/useRoutingUserActiveFloor";
import { storeLocation } from "../../redux/slices/applicationSlice";
import { useDisplayRoute } from "../../hooks/useDisplayRoute";

import DesktopRoutingView from "../RoutingView/views/DesktopRoutingView/DesktopRoutingView";
import MobileRouteView from "./views/MobileRouteView/MobileRouteView";

import RouteContent from "./components/RouteContent/RouteContent";
import TimeToDestination from "../../components/TimeToDestination/TimeToDestination";

import { push } from "../../utils/navigate";
import { buildRoutingPath } from "../../utils/buildRoutingPath";
import { QueryParamBinary, QueryParams, RoutingPath } from "../../utils/types";
import { showRouteTime } from "../../utils/showRouteTime";
import useResponsive from "../../hooks/useResponsive";
import styles from "./RouteView.module.scss";
import { MapContext } from "../../contexts/MapContext";
import {
  useGetMapConfigQuery,
  useGetFeatureByUidQuery,
  useGetFeatureByIdQuery,
} from "../../redux/services/vectormapsAPI";
import { useTranslation } from "react-i18next";
import { SnackBar } from "@livingmap/hippo";
import { FloorConfig } from "../../components/Map/plugins/floor-control";

export default function RouteView() {
  const {
    chipContainerRef,
    floorControl,
    routingControl,
    mapLoaded,
    clusteredPinControl,
  } = useContext(MapContext);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { fromId, toId, fromUid, toUid, floorName } = useParams();
  const { Mobile } = useResponsive();
  const { t } = useTranslation();

  const [clearedRoute, setClearedRoute] = useState(false);
  const [activeFloor, setActiveFloor] = useState<FloorConfig | undefined>(
    undefined,
  );
  const { updateRoutingUserActiveFloor } = useRoutingUserActiveFloor({
    getActiveFloor: () => floorControl?.getActiveFloor(),
  });
  const [displayFloorSnack, setDisplayFloorSnack] = useState(false);
  const { data: mapData } = useGetMapConfigQuery();

  const { language, project } = useAppSelector((state) => state.application);

  const splitFromId = fromId?.split(",");
  const splitToId = toId?.split(",");

  const re = /^[0-9]+$/;
  const {
    isLoading: isOriginLoading,
    isSuccess: isOriginSuccess,
    data: originData,
  } = useGetFeatureByUidQuery(
    { featureUid: Number(fromUid)!, project },
    { skip: !fromUid || !re.test(fromUid!) || !project },
  );

  const {
    isLoading: isDestinationLoading,
    isSuccess: isDestinationSuccess,
    data: destinationData,
  } = useGetFeatureByUidQuery(
    { featureUid: Number(toUid)!, project },
    { skip: !fromUid || !re.test(toUid!) || !project },
  );

  useEffect(() => {
    if (fromUid && toUid) {
      if (!isOriginLoading && !isDestinationLoading) {
        const origin = isOriginSuccess ? originData.id : fromUid;
        const destination = isDestinationSuccess ? destinationData.id : toUid;
        dispatch(
          push({
            pathOrLocation: buildRoutingPath(RoutingPath.NAVIGATE, {
              fromId: origin,
              toId: destination,
            }),
            discardParams: [QueryParams.QUERY, QueryParams.SEARCH_TAG_TYPE],
          }),
        );
      }
    }
  }, [
    fromUid,
    toUid,
    isOriginLoading,
    isOriginSuccess,
    originData,
    isDestinationLoading,
    isDestinationSuccess,
    destinationData,
    dispatch,
  ]);

  useEffect(() => {
    if (floorControl) {
      if (floorName) {
        const floorObj = floorControl
          .getAllFloors()
          ?.find((floorObj) => floorObj.short_name === floorName);
        if (floorObj) {
          setActiveFloor(floorObj);
          if (floorObj.id !== floorControl.getActiveFloor()?.id)
            setDisplayFloorSnack(true);
        }
      } else {
        const currentFloor = floorControl.getActiveFloor();
        if (currentFloor !== null) setActiveFloor(currentFloor);
      }
    }
  }, [floorName, floorControl]);

  const { data: originByIdData } = useGetFeatureByIdQuery(
    { featureId: fromId!, project },
    {
      skip: !fromId || fromId === "-" || !project || splitFromId?.length === 2,
    },
  );

  const { data: destinationByIdData } = useGetFeatureByIdQuery(
    { featureId: toId!, project },
    { skip: !toId || toId === "-" || !project || splitToId?.length === 2 },
  );
  const {
    data: routeData,
    isFetching,
    isSuccess,
    isError,
    stepFreeRoute,
    routeRequestKey,
  } = usePostRouteData({
    fromId,
    toId,
    floor: activeFloor,
  });

  const { routeMilestones, renderRoute } = useDisplayRoute({
    segments: routeData?.segments,
    sequenceOrder: routeData?.routeMetadata[0].sequenceOrder,
    directionsEnabled: mapData?.routing?.directions_enabled,
    originFeature: originByIdData || splitFromId,
    destinationFeature: destinationByIdData || splitToId,
    intercept: routeData?.intercept,
    activeFloor,
    stepFreeRoute,
  });

  useEffect(() => {
    if (isSuccess && routeMilestones) {
      clusteredPinControl?.clearClusteredPinSource();
      dispatch(storeLocation(location));
    }
  }, [clusteredPinControl, dispatch, isSuccess, location, routeMilestones]);

  // If the returned route is an error, clear the routing control
  // then if the route has been cleared we will rerender the route otherwise we won't
  useEffect(() => {
    if (isError) {
      routingControl?.clear();
      setClearedRoute(true);
    } else {
      if (clearedRoute) renderRoute();
      setClearedRoute(false);
    }
  }, [isError, routingControl, renderRoute, clearedRoute]);

  // StepFreeToggle component to be used in mobile/desktop view
  const StepFreeToggleComponent = () => (
    <ToggleSwitch
      dataQA="avoid-stairs-toggle-switch"
      label={t("navigate_page.avoid_stairs_toggle")}
      onToggle={() => {
        dispatch(
          push({
            pathOrLocation: location.pathname,
            newQueryParams: {
              [QueryParams.STEP_FREE]: stepFreeRoute
                ? QueryParamBinary.DISABLED
                : QueryParamBinary.ENABLED,
            },
          }),
        );
      }}
      isToggled={stepFreeRoute}
      isDisabled={isFetching}
    />
  );

  const showTime =
    routeData &&
    mapData &&
    showRouteTime(
      mapData.routing?.time_to_destination_enabled,
      mapData.routing?.time_to_destination_limit,
      routeData.routeMetadata[0].totalTime,
    );

  const TimeToDestinationComponent = ({
    showGoButton = true,
    className,
    onShare,
  }: {
    showGoButton?: boolean;
    className?: string;
    onShare?: () => void;
  }) =>
    routeData && mapData ? (
      <TimeToDestination
        dataQA="route-time-to-destination"
        kilometres={routeData.routeMetadata[0].totalLength}
        displayUnit={mapData.routing?.display_unit_system || "metric"}
        minutes={routeData.routeMetadata[0].totalTime}
        steps={routeMilestones.length}
        currentStep={0}
        live={fromId?.includes(",") || toId?.includes(",")}
        arrived={false}
        showGoButton={showGoButton}
        className={className}
        showTime={showTime}
        onShare={onShare}
        onClick={() => {
          // we set the routingUserActiveFloor here before clicking 'GO' to ensure that the floor won't change
          // when continuing throughout the rest of the routing screens
          updateRoutingUserActiveFloor();
          routingControl!.setJourneyMode(true);
          dispatch(
            push({
              pathOrLocation: buildRoutingPath(RoutingPath.JOURNEY, {
                fromId,
                toId,
              }),
              newQueryParams: {
                [QueryParams.STEP_FREE]: stepFreeRoute
                  ? QueryParamBinary.ENABLED
                  : QueryParamBinary.DISABLED,
              },
            }),
          );
        }}
      />
    ) : null;

  const handleSwapButtonClick = () => {
    dispatch(
      push({
        pathOrLocation: buildRoutingPath(RoutingPath.NAVIGATE, {
          fromId: toId,
          toId: fromId,
        }),
      }),
    );
  };

  return (
    <>
      <RouteInterceptDialog
        intercept={routeData?.intercept}
        requestKey={routeRequestKey}
      />
      {Mobile && displayFloorSnack && (
        <div className={styles.snackContainer}>
          <SnackBar
            text={"Floor changed to match your location"}
            dataQA="floor-select-snackbar"
            onClose={() => {
              setDisplayFloorSnack(false);
            }}
            className={styles.snackBar}
          />
        </div>
      )}
      <DesktopRoutingView
        intercept={routeData?.intercept}
        chipContainerRef={chipContainerRef}
        isFetchingRoute={isFetching}
        isRouteSuccess={isSuccess}
        routeMilestones={routeMilestones}
        totalTime={routeData?.routeMetadata[0].totalTime || 0}
        timeToDestinationComponent={
          !isError ? (
            <TimeToDestinationComponent
              showGoButton={false}
              className={styles.desktopTimeToDestination}
            />
          ) : null
        }
        stepFreeToggleComponent={<StepFreeToggleComponent />}
        mapLoaded={mapLoaded}
        originalRouteFloor={activeFloor}
      />
      <MobileRouteView
        onSwapButtonClick={handleSwapButtonClick}
        isFetchingRoute={isFetching}
        isRouteSuccess={isSuccess}
        stepFreeToggleComponent={<StepFreeToggleComponent />}
        stepFreeRoute={stepFreeRoute}
        timeToDestinationComponent={
          !isError ? <TimeToDestinationComponent /> : null
        }
        originalRouteFloor={activeFloor}
      >
        <RouteContent
          intercept={routeData?.intercept}
          routeMilestones={routeMilestones}
          totalTime={routeData?.routeMetadata[0].totalTime || 0}
          showTime={showTime}
          displayUnit={mapData?.routing?.display_unit_system || "metric"}
          language={language}
          showStepOption={mapData?.routing?.directions_enabled}
        />
      </MobileRouteView>
    </>
  );
}
